import axios from '../utils/axios';

export interface EpisodeListItem {
  s3_key: string;
  filename: string;
  captured_ts: string;
  device_id: string | null;
  cached: boolean;
}

export interface EpisodeTableGroupEntry extends Record<string, unknown> {
  count: number;
  ts_min_ms?: number | null;
  ts_max_ms?: number | null;
}

export interface EpisodeTableSummary {
  present: boolean;
  row_count: number;
  kind?: 'short' | 'large';
  rows?: Record<string, unknown>[];
  ts_min_ms?: number | null;
  ts_max_ms?: number | null;
  groups?: EpisodeTableGroupEntry[] | null;
  sample_rows?: Record<string, unknown>[];
}

export interface EpisodeSummary {
  s3_key: string;
  filename: string;
  device_id: string | null;
  captured_ts: string;
  cached_at: string;
  expires_at: string;
  source: 'cache' | 'downloaded';
  tables: Record<string, EpisodeTableSummary>;
}

export interface EpisodeTableRows {
  s3_key: string;
  table: string;
  kind: 'short' | 'large';
  row_count: number;
  limit: number;
  offset: number;
  rows: Record<string, unknown>[];
}

/** Every team's files captured up to `to`, from `from` — or from the start, without it. */
export async function listEpisodes(from: string | undefined, to: string): Promise<EpisodeListItem[]> {
  return axios
    .get('/episode-db-cache/list', { params: from ? { from, to } : { to } })
    .then((res) => res.data);
}

export async function getEpisodeSummary(s3Key: string): Promise<EpisodeSummary> {
  return axios
    .get(`/episode-db-cache/${s3Key}/summary`)
    .then((res) => res.data);
}

export async function getEpisodeTable(
  s3Key: string,
  table: string,
  opts: { limit?: number; offset?: number } = {},
): Promise<EpisodeTableRows> {
  return axios
    .get(`/episode-db-cache/${s3Key}/table/${table}`, { params: opts })
    .then((res) => res.data);
}

export interface EpisodeEvictResult {
  s3_key: string;
  evicted: boolean;
}

export async function deleteEpisodeCache(s3Key: string): Promise<EpisodeEvictResult> {
  return axios.delete(`/episode-db-cache/${s3Key}`).then((res) => res.data);
}

export interface EpisodeTableCsvExport {
  filename: string;
  csv: string;
  row_count: number;
}

export interface EpisodeTableXlsxExport {
  filename: string;
  content_b64: string;
  row_count: number;
}

export type EpisodeExportFilters = Record<string, string | number>;

export async function exportEpisodeTableCsv(
  s3Key: string,
  table: string,
  filters: EpisodeExportFilters = {},
): Promise<EpisodeTableCsvExport> {
  return axios
    .get(`/episode-db-cache/${s3Key}/table/${table}/csv`, { params: filters })
    .then((res) => res.data);
}

export async function exportEpisodeTableXlsx(
  s3Key: string,
  table: string,
  filters: EpisodeExportFilters = {},
): Promise<EpisodeTableXlsxExport> {
  return axios
    .get(`/episode-db-cache/${s3Key}/table/${table}/xlsx`, { params: filters })
    .then((res) => res.data);
}
